import { inject, Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError, concatMap, filter, first, map, mergeMap, retry, switchMap, tap } from 'rxjs/operators';
import { getConfigurationValue$ } from 'src/app/core/services/api-fns';
import { AuthService, getIsValidUser } from 'src/app/core/services/auth.service';
import { TrackingService } from '../../shared/tracking/tracking.service';
import { IxRestApi } from './api/ix-rest-api';
import { ThirdPartyLicenseService } from './third-party-license.service';

@Injectable({ providedIn: 'root' })
export class UserConfigService {
  readonly #ixRestApi = inject(IxRestApi);
  readonly #licenseService = inject(ThirdPartyLicenseService);
  readonly #trackingService = inject(TrackingService);
  readonly #authService = inject(AuthService);

  // config$ is defined like this because the call is cached and combineLatest causes multiple calls.
  readonly #config$ = getConfigurationValue$(this.#ixRestApi, 'aggrid').pipe(
    concatMap(aggrid =>
      getConfigurationValue$(this.#ixRestApi, 'userpilot').pipe(map(userpilot => [aggrid, userpilot])),
    ),
  );

  initUserConfig() {
    this.#authService.user$
      .pipe(
        filter(getIsValidUser),
        first(), // Load one time only because redoing it is unnecessary and side effects will spam the console on error.
        mergeMap(() => this.#loadConfig()),
      )
      .subscribe();
  }

  #loadConfig() {
    return this.#getConfig().pipe(
      switchMap(() =>
        this.#trackingService
          .init()
          .pipe(retry({ count: 5, delay: 1000 }))
          .pipe(catchError(() => EMPTY)),
      ),
    );
  }

  #getConfig() {
    return this.#config$.pipe(
      tap(([aggrid, userpilot]) => {
        this.#licenseService.setAgGridLicense(aggrid);
        this.#licenseService.setUserpilotLicense(userpilot);
      }),
    );
  }
}
